import { useCallback, useEffect, useRef, useState } from 'react';

import { getNfcScanFailure, isWebNfcAvailable, startNfcScan } from 'lib/webNfc';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

type Options = {
	/** Receives the normalized UID of every card read. */
	onUid: (uid: string) => void;
	/**
	 * Whether to keep reading after a card, for a batch. Otherwise the read stops at the first
	 * card, as it does to find one card.
	 */
	continuous?: boolean;
};

/**
 * Reads NFC cards with Web NFC, from Chrome for Android.
 *
 * `available` is false in any other browser, where the screens offer no reading at all.
 * `start` has to be called from a click: Chrome refuses a read nobody asked for. The read is
 * stopped by `stop`, after the first card unless `continuous`, and when the component using
 * it unmounts, so closing a screen or a modal never leaves a read open.
 */
function useNfcReader({ onUid, continuous = false }: Options) {
	const { readFailureLabel } = useNfcCardLabels();

	const [available] = useState(isWebNfcAvailable);
	const [scanning, setScanning] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const controllerRef = useRef<AbortController | null>(null);

	const onUidRef = useRef(onUid);

	useEffect(() => {
		onUidRef.current = onUid;
	}, [onUid]);

	const stop = useCallback(() => {
		controllerRef.current?.abort();
		controllerRef.current = null;
		setScanning(false);
	}, []);

	const start = useCallback(async () => {
		if (!available) return;

		controllerRef.current?.abort();

		const controller = new AbortController();
		controllerRef.current = controller;

		setError(null);
		setScanning(true);

		try {
			await startNfcScan(controller.signal, {
				onUid: (uid) => {
					setError(null);
					if (!continuous) {
						stop();
					}
					onUidRef.current(uid);
				},
				onFailure: (failure) => setError(readFailureLabel(failure)),
			});
		} catch (scanError) {
			const failure = getNfcScanFailure(scanError);

			// A read stopped while it was starting is not a failure
			if (!failure || controller.signal.aborted) return;

			console.error(scanError);
			setError(readFailureLabel(failure));
			if (controllerRef.current === controller) {
				controllerRef.current = null;
				setScanning(false);
			}
		}
	}, [available, continuous, stop, readFailureLabel]);

	useEffect(() => () => controllerRef.current?.abort(), []);

	return { available, scanning, error, start, stop };
}

export default useNfcReader;
